import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { GraduationCap, FileText, CheckCircle2, Cpu, Clock, Eye } from "lucide-react";
import RichText from "../components/RichText";

const API_PUBLIC = (token) => `/public/rattrapages/${token}`;

export default function PublicPreviewPage() {
  const { token } = useParams();
  const [rat, setRat] = useState(null);
  const [err, setErr] = useState("");
  const [showAnswers, setShowAnswers] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(API_PUBLIC(token));
        setRat(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Aperçu indisponible");
      }
    })();
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa] px-6" data-testid="public-error">
        <div className="bg-white border border-slate-200 rounded-md p-8 max-w-md text-center">
          <div className="font-display text-xl font-semibold text-slate-900">Aperçu indisponible</div>
          <p className="text-sm text-slate-600 mt-2">{err}</p>
          <p className="text-xs text-slate-500 mt-4">Le lien a peut-être été révoqué par l'équipe pédagogique.</p>
        </div>
      </div>
    );
  }
  if (!rat) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm" data-testid="public-loading">Chargement de l'aperçu…</div>;
  }

  const modelLabel = rat.ai_model === "gpt-5.2" ? "GPT-5.2" : "Claude Sonnet 4.5";

  return (
    <div className="min-h-screen bg-[#f8f9fa] bg-grid" data-testid="public-preview-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#0f172a] flex items-center justify-center">
              <GraduationCap size={18} className="text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-[14px] text-slate-900">Aperçu pour relecture</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Lecture seule · Lien partagé par le staff
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showAnswers}
              onChange={(e) => setShowAnswers(e.target.checked)}
              data-testid="toggle-answers"
              className="accent-slate-900"
            />
            Afficher les bonnes réponses
          </label>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="label-xs mb-2">Rattrapage</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="public-title">{rat.title}</h1>
        <p className="text-slate-600 mt-2 text-sm">{rat.module}</p>

        <div className="flex flex-wrap gap-2 mt-4">
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-white border border-slate-200 rounded-sm font-semibold text-slate-700">
            <Clock size={12} /> {rat.duration_minutes || 60} min
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-white border border-slate-200 rounded-sm font-semibold text-slate-700">
            <FileText size={12} /> {rat.questions?.length || 0} questions
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-blue-50 text-[#2563eb] rounded-sm font-semibold">
            <Cpu size={12} /> {modelLabel}
          </span>
        </div>

        <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
          <Eye size={14} className="mt-0.5 shrink-0" />
          <span>Document confidentiel — ne pas diffuser aux étudiants. La correction ne doit pas leur être affichée sur Forms.</span>
        </div>

        <div className="mt-8 space-y-4">
          {(rat.questions || []).map((q, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-md p-6" data-testid={`public-q-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-sm bg-slate-900 text-white text-sm font-bold flex items-center justify-center">{i + 1}</div>
                <div>
                  <div className="label-xs">Question {i + 1}</div>
                  {q.title && <div className="font-display text-lg font-semibold text-slate-900 leading-tight">{q.title}</div>}
                </div>
              </div>
              <RichText className="text-sm text-slate-900">{q.statement}</RichText>
              <ul className="mt-4 space-y-2">
                {(q.options || []).map((opt, j) => {
                  const correct = (q.correct_labels || []).includes(opt.label);
                  return (
                    <li
                      key={j}
                      className={`flex items-start gap-2 px-3 py-2 border rounded-sm text-sm ${
                        showAnswers && correct ? "border-green-300 bg-green-50/60" : "border-slate-200 bg-white"
                      }`}
                    >
                      <span className={`mt-0.5 w-5 h-5 rounded-sm flex items-center justify-center shrink-0 border ${
                        showAnswers && correct ? "bg-[#15803d] border-[#15803d] text-white" : "border-slate-300 bg-white"
                      }`}>
                        {showAnswers && correct ? <CheckCircle2 size={12} /> : null}
                      </span>
                      <span className="font-semibold w-6 shrink-0 text-slate-700">{opt.label}.</span>
                      <span className="text-slate-800 flex-1 whitespace-pre-wrap">{opt.text}</span>
                    </li>
                  );
                })}
              </ul>
              {showAnswers && q.explanation && (
                <div className="mt-4 px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-700">
                  <span className="font-semibold text-slate-900">Explication (interne) — </span>
                  {q.explanation}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center text-xs text-slate-500">
          Merci pour votre relecture. Transmettez vos retours au staff de l'école pour intégration dans la version finale.
        </div>
      </main>
    </div>
  );
}
